import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import shopService from "../services/shopService";

const initialForm = {
  full_name: "",
  username: "",
  password: "",
  mobile: "",
  shop_name: "",
  address: "",
};

const CreateShopModal = ({
  show,
  onClose,
  onShopCreated,
}) => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      document.body.classList.add("modal-open");
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.classList.remove("modal-open");
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [show]);

  if (!show) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "mobile") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setFormData((previous) => ({
        ...previous,
        mobile: digitsOnly,
      }));
    } else {
      setFormData((previous) => ({
        ...previous,
        [name]: value,
      }));
    }
  };

  const resetAndClose = () => {
    if (loading) {
      return;
    }

    setFormData(initialForm);
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = {
      full_name: formData.full_name.trim(),
      username: formData.username.trim(),
      password: formData.password,
      mobile: formData.mobile.trim(),
      shop_name: formData.shop_name.trim(),
      address: formData.address.trim(),
    };

    if (
      !payload.full_name ||
      !payload.username ||
      !payload.password ||
      !payload.shop_name
    ) {
      toast.error(
        "Owner name, username, password and shop name are required."
      );
      return;
    }

    if (payload.username.length < 3) {
      toast.error(
        "Username must contain at least 3 characters."
      );
      return;
    }

    if (payload.password.length < 6) {
      toast.error(
        "Password must contain at least 6 characters."
      );
      return;
    }

    if (
      payload.mobile &&
      (payload.mobile.length !== 10 || !/^[6-9]\d{9}$/.test(payload.mobile))
    ) {
      toast.error(
        "Please enter a valid 10-digit mobile number (starting with 6, 7, 8, or 9)."
      );
      return;
    }

    try {
      setLoading(true);

      const response = await shopService.createShop(payload);

      if (!response?.success) {
        toast.error(
          response?.message || "Unable to create shop."
        );
        return;
      }

      toast.success(
        response.message || "Shop created successfully."
      );

      setFormData(initialForm);

      await Promise.resolve(onShopCreated?.());

      onClose?.();
    } catch (error) {
      console.error("Create Shop Error:", error);

      toast.error(
        error.response?.data?.message ||
          "Unable to create shop."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={resetAndClose}
      />

      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="createShopModalTitle"
        style={{ zIndex: 1055, overflow: "hidden" }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            resetAndClose();
          }
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg my-auto mx-auto px-2 px-sm-3"
          style={{ maxWidth: "600px" }}
        >
          <div className="modal-content border border-[rgba(148,163,184,0.2)] bg-[#121827] shadow-2xl rounded-2xl overflow-hidden text-[#F8FAFC]">
            {/* HEADER */}
            <div className="modal-header bg-[#0D111C] border-b border-[rgba(148,163,184,0.14)] py-3 px-4 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 text-lg font-bold">
                  <i className="bi bi-shop" />
                </div>
                <div>
                  <h5
                    id="createShopModalTitle"
                    className="modal-title fw-bold text-[#F8FAFC] text-base mb-0 leading-tight"
                  >
                    Create New Shop
                  </h5>
                  <div className="text-[11px] text-[#94A3B8] leading-tight">
                    Shop login account will also be created automatically
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={resetAndClose}
                disabled={loading}
                aria-label="Close"
              />
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body p-4 space-y-4">
                {/* SHOP INFORMATION SECTION */}
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[#D4AF37] mb-2 d-flex align-items-center gap-1.5">
                    <i className="bi bi-building" />
                    Shop Details
                  </div>

                  <div className="row g-2.5 g-sm-3">
                    <div className="col-6">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block truncate">
                        Shop Name *
                      </label>
                      <input
                        type="text"
                        name="shop_name"
                        className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                        placeholder="Shop name"
                        value={formData.shop_name}
                        onChange={handleChange}
                        maxLength={150}
                        disabled={loading}
                        required
                      />
                    </div>

                    <div className="col-6">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block truncate">
                        Address
                      </label>
                      <input
                        type="text"
                        name="address"
                        className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                        placeholder="Shop address"
                        value={formData.address}
                        onChange={handleChange}
                        maxLength={255}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-[rgba(148,163,184,0.12)] my-2" />

                {/* OWNER LOGIN SECTION */}
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[#D4AF37] mb-2 d-flex align-items-center gap-1.5">
                    <i className="bi bi-person-badge" />
                    Shop Owner Account Info
                  </div>

                  <div className="row g-2.5 g-sm-3">
                    <div className="col-6">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block truncate">
                        Owner Name *
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                        placeholder="Owner name"
                        value={formData.full_name}
                        onChange={handleChange}
                        maxLength={100}
                        disabled={loading}
                        required
                      />
                    </div>

                    <div className="col-6">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block truncate">
                        Mobile Number
                      </label>
                      <input
                        type="tel"
                        name="mobile"
                        className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                        placeholder="10-digit mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        maxLength={10}
                        inputMode="numeric"
                        disabled={loading}
                      />
                    </div>

                    <div className="col-6">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block truncate">
                        Username *
                      </label>
                      <input
                        type="text"
                        name="username"
                        className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                        placeholder="Username"
                        value={formData.username}
                        onChange={handleChange}
                        minLength={3}
                        maxLength={50}
                        autoComplete="off"
                        disabled={loading}
                        required
                      />
                    </div>

                    <div className="col-6">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block truncate">
                        Password *
                      </label>
                      <input
                        type="password"
                        name="password"
                        className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleChange}
                        minLength={6}
                        autoComplete="new-password"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div className="modal-footer bg-[#0D111C] border-t border-[rgba(148,163,184,0.14)] py-3 px-4 d-flex justify-content-end gap-2.5">
                <button
                  type="button"
                  className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#121827] px-4 py-2.5 text-xs font-semibold text-[#94A3B8] transition hover:bg-[#182033] hover:text-[#F8FAFC]"
                  onClick={resetAndClose}
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] px-5 py-2.5 text-xs font-extrabold text-[#070A12] shadow-md shadow-[#D4AF37]/10 transition hover:opacity-90 disabled:opacity-50 d-flex align-items-center justify-content-center gap-1.5"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-circle" />
                      Create Shop
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateShopModal;